import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, FlaskConical, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useMyProgress } from './api'
import { lessonByKey, type Block, type Lesson } from './content'
import { LessonStatusIcon, QuizScore } from './lesson-status'
import { lessonState } from './lesson-state'
import { Quiz } from './quiz'
import { startPractice } from './practice/engine'
import { stepHref } from './practice/steps'

function LessonBlock({ block }: { block: Block }) {
  switch (block.kind) {
    case 'heading':
      return <h2 className="pt-2 text-base font-semibold">{block.text}</h2>
    case 'text':
      return <p className="text-[15px] leading-relaxed">{block.text}</p>
    case 'steps':
      return (
        <ol className="list-decimal space-y-1.5 pl-5 text-[15px] leading-relaxed marker:text-muted-foreground">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      )
    case 'rule':
      return (
        <div className="grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-4">
          <div className="text-sm font-semibold">{block.title}</div>
          <div className="text-[15px] leading-relaxed text-muted-foreground">{block.text}</div>
        </div>
      )
    case 'image':
      return (
        <figure className={cn('space-y-2', block.phone && 'max-w-[18rem]')}>
          <img src={block.src} alt={block.alt} loading="lazy" className="w-full rounded-lg border bg-muted/30" />
          <figcaption className="text-sm text-muted-foreground">{block.caption}</figcaption>
        </figure>
      )
  }
}

function PracticePanel({ lesson }: { lesson: Lesson & { practice: NonNullable<Lesson['practice']> } }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const start = () => {
    startPractice(lesson.key, lesson.practice.scenario)
    queryClient.removeQueries()
    const first = lesson.practice.steps[0]
    // A recorded scenario's links are known once it loads; the panel opens them from there.
    const href = first ? stepHref(first, null) : null
    if (href) void navigate({ to: href })
  }
  return (
    <section aria-labelledby="practice-heading" className="space-y-3 rounded-xl border bg-background p-4">
      <h2 id="practice-heading" className="flex items-center gap-2 text-base font-semibold">
        <FlaskConical className="size-4" aria-hidden="true" /> Practice
      </h2>
      <ol className="space-y-2 text-sm text-muted-foreground">
        {lesson.practice.steps.map((step, i) => (
          <li key={step.text} className="flex gap-2">
            <span className="w-4 shrink-0 tabular-nums">{i + 1}.</span>
            <span>{step.text}</span>
          </li>
        ))}
      </ol>
      <Button size="sm" onClick={start} className="bg-emerald-600 text-white hover:bg-emerald-700">
        <Play className="size-3.5" /> Start practice
      </Button>
    </section>
  )
}

export function LessonPage({ lessonKey }: { lessonKey: string }) {
  const lesson = lessonByKey(lessonKey)
  const progress = useMyProgress()
  if (!lesson) {
    return (
      <div className="mx-auto max-w-4xl space-y-3">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Learn
        </Link>
        <p>This lesson doesn’t exist anymore.</p>
      </div>
    )
  }
  const row = progress.data?.get(lesson.key)
  const practice = lesson.practice

  return (
    <div className={cn('mx-auto space-y-5', practice ? 'max-w-5xl' : 'max-w-3xl')}>
      <div className="space-y-1">
        <Link to="/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-4" /> Learn
        </Link>
        <div className="flex items-start gap-2.5">
          <h1 className="text-2xl font-bold tracking-tight">{lesson.title}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <LessonStatusIcon state={lessonState(row)} />
            {row?.completed_at ? 'Finished' : `${lesson.minutes} min`}
          </span>
          <QuizScore score={row?.quiz_score} />
        </div>
      </div>

      <div className={cn('grid gap-6', practice && 'lg:grid-cols-[minmax(0,1fr)_20rem]')}>
        <article className="min-w-0 space-y-4 rounded-xl border bg-background p-5 md:p-6">
          {lesson.body.map((block, i) => (
            <LessonBlock key={i} block={block} />
          ))}
        </article>
        {/* After the reading on a phone, beside it on a wide screen. */}
        {practice && (
          <aside className="lg:sticky lg:top-0 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start">
            <PracticePanel lesson={{ ...lesson, practice }} />
          </aside>
        )}
        {lesson.quiz && (
          <div className="min-w-0 rounded-xl border bg-background p-5 md:p-6">
            <Quiz lessonKey={lesson.key} questions={lesson.quiz} />
          </div>
        )}
      </div>
    </div>
  )
}
